// HelpSearch.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MdEmail, MdPhone } from "react-icons/md";
import carIcon from "../assets/images/car1.png";
import driverIcon from "../assets/images/driver2.png";
import placesIcon from "../assets/images/places.png";
import { useLanguage } from "../context/LanguageContext";

const HelpSearch = () => {
  const [query, setQuery] = useState("");
  const [selectedTab, setSelectedTab] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showContact, setShowContact] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const th = t.help;

  const tabIcons = [carIcon, driverIcon, placesIcon];
  const tabs = th.tabs.map((tab, i) => ({ ...tab, icon: tabIcons[i] }));

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleTabClick = (label, path) => {
    setSelectedTab(label);
    navigate(path);
  };

  const handleMailClick = (e) => {
    e.preventDefault();
    const to = "support@berymo.com";
    const subject = encodeURIComponent("Support Request");
    const body = encodeURIComponent(
      "Hello Berymo Support Team,\n\nI need assistance with:\n\nThank you!"
    );
    const gmailUrl = `https://mail.google.com/mail/u/0/?view=cm&fs=1&tf=cm&to=${to}&su=${subject}&body=${body}`;
    const newWindow = window.open(
      gmailUrl,
      "_blank",
      "noopener,noreferrer,width=800,height=600"
    );
    if (newWindow) newWindow.focus();
  };

  const handleContactClick = () => {
    setShowContact(!showContact);
  };

  // --- Styles ---
  const headerContainerStyle = {
    padding: isMobile ? "24px" : "44px",
    textAlign: "center",
    position: "relative",
  };

  const titleStyle = {
    fontSize: "28px",
    fontWeight: "bold",
    marginBottom: "26px",
    marginTop: "100px",
  };

  const contactContainerStyle = {
    position: "absolute",
    top: isMobile ? "80px" : "60px",
    right: isMobile ? "10px" : "50px",
    display: "flex",
    flexDirection: isMobile ? "row" : "row", // stack on mobile
    alignItems: isMobile ? "flex-end" : "center",
    gap: isMobile ? "10px" : "16px",
    zIndex: 100,
  };

  const contactItemStyle = {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#fff",
    borderRadius: "20px",
    padding: "6px 10px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    cursor: "pointer",
    fontSize: isMobile ? "12px" : "14px",
    fontWeight: "500",
    gap: "6px",
    transition: "0.3s",
  };

  const popupStyle = {
    position: "absolute",
    top: isMobile ? "50px" : "60px",
    right: 0,
    backgroundColor: "#fff",
    padding: "12px 16px",
    borderRadius: "10px",
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
    zIndex: 1000,
    textAlign: "center",
  };

  const callButtonStyle = {
    marginTop: "8px",
    backgroundColor: "#000",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "6px 12px",
    cursor: "pointer",
  };

  const inputWrapperStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #ccc",
    borderRadius: "8px",
    padding: isMobile ? "4px" : "1px",
    maxWidth: isMobile ? "90%" : "360px",
    margin: "0 auto",
    backgroundColor: "#fff",
  };

  const inputStyle = {
    flex: 1,
    border: "none",
    outline: "none",
    marginLeft: "0px",
    marginBottom: "5px",
    padding: "6px",
    fontSize: isMobile ? "14px" : "15px",
    textAlign: "center",
    lineHeight: isMobile ? "1.0" : "2",
  };

  const tabContainerStyle = {
    display: "flex",
    justifyContent: "center",
    gap: isMobile ? "20px" : "80px",
    padding: isMobile ? "24px" : "150px",
    flexDirection: isMobile ? "column" : "row",
    flexWrap: "wrap",
    alignItems: "center",
  };

  const tabBoxStyle = (label) => ({
    width: isMobile ? "90%" : "200px",
    maxWidth: "300px",
    height: isMobile ? "auto" : "200px",
    backgroundColor: "#FEF4F5",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    cursor: "pointer",
    padding: "20px",
    boxShadow: selectedTab === label ? "0 0 0 2px black" : "none",
    textAlign: "center",
    margin: isMobile ? "0 auto" : "0",
  });

  const iconStyle = {
    width: "50px",
    height: isMobile ? "40px" : "50px",
    marginBottom: isMobile ? "8px" : "12px",
    backgroundColor: "#FEF4F5",
  };

  const backgroundSectionStyle = {
    backgroundColor: "#FEF4F5",
    paddingBottom: "50px",
  };

  return (
    <>
      <div style={backgroundSectionStyle}>
        <div style={headerContainerStyle}>
          {/* Top Right Mail + Contact */}
          <div style={contactContainerStyle}>
            <div style={contactItemStyle} onClick={handleMailClick}>
              <MdEmail size={18} color="#000" />
              {th.mailUs}
            </div>

            <div style={contactItemStyle} onClick={handleContactClick}>
              <MdPhone size={18} color="#000" />
              {th.contactUs}
            </div>

            {showContact && (
              <div style={popupStyle}>
                <p style={{ margin: 0 }}>📞 +1 (866) 423-7966</p>
                <button
                  style={callButtonStyle}
                  onClick={() => (window.location.href = "tel:+18664237966")}
                >
                  {th.callNow}
                </button>
              </div>
            )}
          </div>

          <h1 style={titleStyle}>{th.title}</h1>
          <div style={inputWrapperStyle}>
            <input
              type="text"
              placeholder={th.searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div style={tabContainerStyle}>
        {tabs.map((tab) => (
          <div
            key={tab.label}
            onClick={() => handleTabClick(tab.label, tab.path)}
            style={tabBoxStyle(tab.label)}
          >
            <img src={tab.icon} alt={tab.label} style={iconStyle} />
            {tab.label}
          </div>
        ))}
      </div>
    </>
  );
};

export default HelpSearch;
